/* Command palette — keyboard-first operation for expert users (FR-UI-11).
 * Pattern adopted from 21st.dev command-palette research (DESIGN-SYSTEM.md §1).
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { CornerDownLeft, Search } from 'lucide-react'
import { useDeck } from '../state/store'

interface Action {
  id: string
  label: string
  group: string
  hint?: string
  run: () => void
}

export function CommandPalette() {
  const open = useDeck((s) => s.paletteOpen)
  const setOpen = useDeck((s) => s.setPaletteOpen)
  const entities = useDeck((s) => s.entities)
  const contacts = useDeck((s) => s.contacts)
  const select = useDeck((s) => s.select)
  const setViewMode = useDeck((s) => s.setViewMode)
  const toggleRunning = useDeck((s) => s.toggleRunning)
  const toggleLayer = useDeck((s) => s.toggleLayer)
  const setDetailTab = useDeck((s) => s.setDetailTab)

  const [query, setQuery] = useState('')
  const [cursor, setCursor] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  const actions = useMemo<Action[]>(() => {
    const a: Action[] = [
      { id: 'pause', label: 'Pause / resume simulation', group: 'Simulation', hint: 'Space', run: toggleRunning },
      { id: '2d', label: 'Switch to 2D tactical view', group: 'View', run: () => setViewMode('2d') },
      { id: '3d', label: 'Switch to 3D globe', group: 'View', run: () => setViewMode('3d') },
      { id: 'sensors', label: 'Toggle sensor range envelopes', group: 'Layers', run: () => toggleLayer('sensorRanges') },
      { id: 'weapons', label: 'Toggle weapon / DLZ envelopes', group: 'Layers', run: () => toggleLayer('weaponEnvelopes') },
      { id: 'truth', label: 'Toggle truth overlay (umpire)', group: 'Layers', run: () => toggleLayer('truth') },
      { id: 'trace', label: 'Show decision trace for selection', group: 'Analysis', run: () => setDetailTab('trace') },
      { id: 'doctrine', label: 'Show doctrine chain for selection', group: 'Analysis', run: () => setDetailTab('doctrine') },
    ]
    for (const e of entities) {
      a.push({
        id: `e-${e.id}`,
        label: e.designation,
        group: 'Units',
        hint: e.className,
        run: () => select({ kind: 'entity', id: e.id }),
      })
    }
    for (const c of contacts) {
      a.push({
        id: `c-${c.id}`,
        label: c.track,
        group: 'Contacts',
        hint: c.classification,
        run: () => select({ kind: 'contact', id: c.id }),
      })
    }
    return a
  }, [entities, contacts, select, setViewMode, toggleRunning, toggleLayer, setDetailTab])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return actions.slice(0, 10)
    return actions
      .filter((x) => `${x.label} ${x.group} ${x.hint ?? ''}`.toLowerCase().includes(q))
      .slice(0, 12)
  }, [actions, query])

  useEffect(() => {
    if (open) {
      setQuery('')
      setCursor(0)
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }, [open])

  if (!open) return null

  const commit = (i: number) => {
    filtered[i]?.run()
    setOpen(false)
  }

  return (
    <div
      className="absolute inset-0 z-50 flex items-start justify-center pt-[14vh]"
      style={{ background: 'rgba(5,8,14,0.55)', backdropFilter: 'blur(2px)' }}
      onClick={() => setOpen(false)}
    >
      <div
        className="panel w-[520px] overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-center gap-2 px-3 py-2.5"
          style={{ borderBottom: '1px solid var(--color-line-2)' }}
        >
          <Search size={14} style={{ color: 'var(--color-text-3)' }} aria-hidden />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setCursor(0)
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault()
                setCursor((c) => Math.min(c + 1, filtered.length - 1))
              } else if (e.key === 'ArrowUp') {
                e.preventDefault()
                setCursor((c) => Math.max(c - 1, 0))
              } else if (e.key === 'Enter') {
                commit(cursor)
              } else if (e.key === 'Escape') {
                setOpen(false)
              }
            }}
            placeholder="Search units, tracks, actions…"
            className="flex-1 bg-transparent outline-none"
            style={{ fontSize: 13, color: 'var(--color-text-1)' }}
            aria-label="Search"
          />
          <kbd
            className="num rounded px-1.5 py-0.5"
            style={{ fontSize: 9.5, background: 'var(--color-surface-3)', color: 'var(--color-text-3)' }}
          >
            ESC
          </kbd>
        </div>

        <div className="max-h-80 overflow-y-auto py-1" role="listbox">
          {filtered.length === 0 && (
            <div className="px-3 py-6 text-center" style={{ fontSize: 12, color: 'var(--color-text-3)' }}>
              No matches for “{query}”.
            </div>
          )}
          {filtered.map((x, i) => (
            <button
              key={x.id}
              role="option"
              aria-selected={i === cursor}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left"
              style={{ background: i === cursor ? 'var(--color-accent-dim)' : undefined }}
              onMouseEnter={() => setCursor(i)}
              onClick={() => commit(i)}
            >
              <span className="label shrink-0" style={{ fontSize: 8.5, minWidth: 58 }}>
                {x.group}
              </span>
              <span className="min-w-0 flex-1 truncate" style={{ fontSize: 12, color: 'var(--color-text-1)' }}>
                {x.label}
              </span>
              {x.hint && (
                <span className="num shrink-0 truncate" style={{ fontSize: 10, color: 'var(--color-text-3)', maxWidth: 160 }}>
                  {x.hint}
                </span>
              )}
              {i === cursor && (
                <CornerDownLeft size={11} style={{ color: 'var(--color-accent-hi)' }} aria-hidden />
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
